"use client";
/* eslint-disable react-hooks/rules-of-hooks */
import { useRef, useState } from "react";

import { useSafeEffect } from "./use-safe-effect";

export interface DebouncedValueOptions {
  millis?: number;
  leading?: boolean;
  initializeValue?: boolean;
}

export function useDebouncedValue<T>(
  value: T,
  { initializeValue = true, leading = false, millis = 300 }: DebouncedValueOptions = {}
) {
  return leading
    ? useLeadingDebounce<T>(value, { millis })
    : useTrailingDebounce<T>(value, { initializeValue, millis });
}

function useLeadingDebounce<T>(value: T, { millis }: { millis: number }) {
  const blockedRef = useRef<boolean>(false);
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useSafeEffect(
    (isMountedRef: React.MutableRefObject<boolean>) => {
      if (isMountedRef.current && !blockedRef.current) {
        blockedRef.current = true;

        setDebouncedValue(value);

        setTimeout(() => {
          blockedRef.current = false;
        }, millis);
      }
    },
    [millis, value]
  );

  return debouncedValue;
}

function useTrailingDebounce<T>(value: T, { initializeValue, millis }: { initializeValue: boolean; millis: number }) {
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [debouncedValue, setDebouncedValue] = useState<T | undefined>(initializeValue ? value : undefined);

  useSafeEffect(
    (isMountedRef: React.MutableRefObject<boolean>) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      timerRef.current && clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => isMountedRef.current && setDebouncedValue(value), millis);
    },
    [millis, value]
  );

  return debouncedValue;
}

